import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Copy, Download, RefreshCw, Loader2, Facebook, Linkedin, Instagram, MessageCircle, Users2, Store, Building2, Megaphone } from "lucide-react";
import { toast } from "sonner";
import FbGroupShare from "@/components/FbGroupShare";

const AUD = {
  consumer: { label: "Consommateurs", Icon: Users2, color: "from-amber-400 to-[#E05638]" },
  pro: { label: "Partenaires pros", Icon: Store, color: "from-sky-400 to-blue-600" },
  organization: { label: "Organismes & CSE", Icon: Building2, color: "from-emerald-400 to-teal-600" },
};

function fullText(p) {
  return [p.text, (p.hashtags || []).join(" ")].filter(Boolean).join("\n\n");
}

function PostCard({ post, onRegen, regening }) {
  const meta = AUD[post.audience] || AUD.consumer;
  const Icon = meta.Icon;
  const shareUrl = post.public_share_url || window.location.origin;
  const txt = fullText(post);
  const [showGroups, setShowGroups] = useState(false);

  const copy = async () => {
    try { await navigator.clipboard.writeText(txt); toast.success("Texte copié — prêt à coller !"); }
    catch { toast.message("Copie manuelle requise."); }
  };
  const download = () => {
    if (!post.image_url) return;
    const a = document.createElement("a");
    a.href = post.image_url; a.download = `proxireduc-${post.audience}.png`; a.target = "_blank";
    document.body.appendChild(a); a.click(); a.remove();
  };
  const openFb = () => { try { navigator.clipboard.writeText(txt); } catch (e) { void e; } window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(txt)}`, "_blank", "noopener"); };
  const openLinkedin = () => { try { navigator.clipboard.writeText(txt); } catch (e) { void e; } toast.success("Texte copié — collez-le dans LinkedIn."); window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener"); };
  const openInsta = () => { try { navigator.clipboard.writeText(txt); } catch (e) { void e; } toast.success("Légende copiée + visuel à télécharger, puis collez sur Instagram."); window.open("https://www.instagram.com/", "_blank", "noopener"); };
  const openWa = () => { window.open(`https://wa.me/?text=${encodeURIComponent(txt + "\n" + shareUrl)}`, "_blank", "noopener"); };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col" data-testid={`social-card-${post.audience}`}>
      <div className={`px-4 py-2.5 text-white font-semibold text-sm flex items-center gap-2 bg-gradient-to-r ${meta.color}`}>
        <Icon size={16} /> {meta.label}
        <span className="ml-auto text-[10px] font-medium bg-white/25 px-2 py-0.5 rounded-full truncate max-w-[45%]" title={post.format}>{post.format}</span>
      </div>
      {post.image_url
        ? <img src={post.image_url} alt={meta.label} className="w-full aspect-square object-cover bg-slate-100" data-testid={`social-image-${post.audience}`} />
        : <div className="w-full aspect-square grid place-items-center bg-slate-100 text-slate-300"><Megaphone size={40} /></div>}
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-sm text-slate-700 whitespace-pre-line flex-1" data-testid={`social-text-${post.audience}`}>{post.text}</p>
        {(post.hashtags || []).length > 0 && (
          <div className="mt-2 text-xs font-semibold text-[#1877F2] break-words">{post.hashtags.join(" ")}</div>
        )}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button onClick={copy} className="btn-shine text-white" data-testid={`social-copy-${post.audience}`}><Copy size={14} className="mr-1.5" /> Copier</Button>
          <Button onClick={download} variant="outline" disabled={!post.image_url} data-testid={`social-download-${post.audience}`}><Download size={14} className="mr-1.5" /> Visuel</Button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <button onClick={openFb} data-testid={`social-fb-${post.audience}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1877F2] text-white text-xs font-semibold hover:opacity-90"><Facebook size={14} /> Facebook</button>
          <button onClick={() => setShowGroups((v) => !v)} data-testid={`social-groups-${post.audience}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:opacity-90"><Users2 size={14} /> Groupes</button>
          <button onClick={openLinkedin} data-testid={`social-linkedin-${post.audience}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0A66C2] text-white text-xs font-semibold hover:opacity-90"><Linkedin size={14} /> LinkedIn</button>
          <button onClick={openInsta} data-testid={`social-instagram-${post.audience}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-fuchsia-500 to-amber-500 text-white text-xs font-semibold hover:opacity-90"><Instagram size={14} /> Instagram</button>
          <button onClick={openWa} data-testid={`social-whatsapp-${post.audience}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:opacity-90"><MessageCircle size={14} /> WhatsApp</button>
        </div>
        {showGroups && <FbGroupShare text={txt} shareUrl={shareUrl} />}
        <button onClick={() => onRegen(post)} disabled={regening} data-testid={`social-regen-${post.audience}`}
                className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#E05638]">
          {regening ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Regénérer cette publication
        </button>
      </div>
    </div>
  );
}

export default function AdminSocialStudio() {
  const [data, setData] = useState({ items: [], dates: [] });
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);
  const [regenId, setRegenId] = useState(null);

  const load = (d) => {
    setLoading(true);
    api.get(`/admin/social-posts${d ? `?date=${d}` : ""}`).then((r) => {
      setData(r.data); setDate(r.data.date);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGen(true);
    const today = new Date().toISOString().slice(0, 10);
    try {
      await api.post("/admin/social-posts/generate");
      toast.info("Génération lancée… vos publications apparaissent dans un instant.");
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data: r } = await api.get(`/admin/social-posts?date=${today}`);
          setData((s) => ({ ...s, items: r.items, dates: r.dates })); setDate(r.date);
          if ((r.items?.length || 0) >= 3) { setGen(false); toast.success("3 publications prêtes à partager !"); return; }
        } catch (e) { void e; }
        if (tries > 40) { setGen(false); return; }
        setTimeout(poll, 5000);
      };
      setTimeout(poll, 4000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération indisponible"); setGen(false); }
  };

  const regen = async (post) => {
    setRegenId(post.id);
    try {
      const { data: np } = await api.post(`/admin/social-posts/${post.id}/regenerate`);
      setData((s) => ({ ...s, items: s.items.map((p) => (p.id === post.id ? np : p)) }));
      toast.success("Publication regénérée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setRegenId(null); }
  };

  const subTab = ({ isActive }) => `px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${isActive ? "bg-[#E05638] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`; // eslint-disable-line no-unused-vars

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold flex items-center gap-2" data-testid="admin-social-title">
            <Megaphone className="text-[#E05638]" /> Studio réseaux sociaux
          </h1>
          <p className="text-sm text-slate-500 mt-1">3 publications prêtes à partager chaque jour — consommateurs, pros, organismes.</p>
        </div>
        <Button onClick={generate} disabled={gen} className="btn-shine text-white" data-testid="admin-social-generate">
          {gen ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Génération…</> : <><Sparkles size={15} className="mr-1.5" /> Générer les publications du jour</>}
        </Button>
      </div>

      {/* Sous-onglets déplacés dans le menu latéral (section « Réseaux sociaux ») */}

      {(data.dates || []).length > 1 && (
        <div className="mt-4 flex items-center gap-2 text-sm">
          <span className="text-slate-500">Jour :</span>
          <select value={date} onChange={(e) => load(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1" data-testid="social-date-select">
            {data.dates.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      )}

      <div className="mt-6">
        {loading ? (
          <div className="py-16 text-center text-slate-400" data-testid="social-loading"><Loader2 className="inline animate-spin mr-2" /> Chargement…</div>
        ) : data.items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="social-empty">
            <Megaphone className="mx-auto text-slate-300" size={40} />
            <p className="mt-3 text-slate-600 font-semibold">Aucune publication pour ce jour.</p>
            <p className="text-sm text-slate-500">Cliquez sur « Générer les publications du jour » pour créer vos 3 posts.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="social-grid">
            {data.items.map((p) => (
              <PostCard key={p.id} post={p} onRegen={regen} regening={regenId === p.id} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
